/* eslint-disable react/no-unescaped-entities */
import CustomModal from '@components/CustomModal';
import AddCalendarTriggerForm from '@components/drawers/AddCalendarTriggerForm';
import AddEventTriggerForm from '@components/drawers/AddEventTriggerForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import ConfirmLeaveModal from '@components/modals/ConfirmLeaveModal';
import WolfButton from '@components/ui/WolfButton';
import { CalendarTriggerIcon20 } from '@components/ui/WolfIcons';
import WolfTriggerBox from '@components/ui/WolfTriggerBox';
import { graphQlClient } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import TriggersGraphQL from '@graphql/trigger.queries';
import { useTranslation } from '@hooks/useTranslation';
import useUnsavedChangesWarning from '@hooks/useUnsavedChangesWarning';
import { SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import { Process } from '@models/process.model';
import {
  CalendarTrigger,
  createOrUpdateTriggerRequest,
  deleteTriggerRequest,
  EventTrigger,
  Trigger,
  TriggerType
} from '@models/trigger.model';
import { AddOutlined, ElectricBoltOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Icon, List, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { RootState } from '@redux/store';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt } from 'react-router-dom';

interface AddTriggerFormProps {
  onClose: () => void;
  initialData?: Process;
}

const AddTriggerForm: React.FC<AddTriggerFormProps> = ({ onClose, initialData }) => {
  const localeProcessForm = useTranslation('processForm');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const dispatch = useDispatch();
  const { organizationId, onboardingDetails } = useAppSelector((state: RootState) => state.organization);

  const [entityData, setEntityData] = useState<Partial<Process>>({
    name: '',
    goal: '',
    category: '',
    serviceId: '',
  });
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [addTrigger, setAddTrigger] = useState<TriggerType | null>(null);
  const [selectedEventTrigger, setSelectedEventTrigger] = useState<EventTrigger>();
  const [selectedCalendarTrigger, setSelectedCalendarTrigger] = useState<Trigger>();
  const [triggerSaving, setTriggerSaving] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState<boolean>(false);
  useUnsavedChangesWarning({ showWarning: hasUnsavedChanges });

  useEffect(() => {
    if (initialData) {
      setEntityData(initialData);
      if (initialData.triggers) {
        setTriggers(initialData.triggers);
      }
    }
  }, [initialData]);

  const _onClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmLeaveModal(true);
    } else {
      setHasUnsavedChanges(false);
      onClose();
    }
  };

  const closeConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
  };

  const closeAndConfirmLeaveModal = () => {
    setShowConfirmLeaveModal(false);
    onClose();
  };

  const updateOrganizationTriggerAdded = async () => {
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            onboardingDetails: {
              ...onboardingDetails,
              triggerAdded: true,
            },
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch (error) {
      console.error('Failed to update trigger added status:', error);
    }
  };

  // TODO: Call this from components
  const handleAddTrigger = async (calendarTrigger?: CalendarTrigger, eventTrigger?: EventTrigger) => {
    setTriggerSaving(true);
    if (!calendarTrigger && !eventTrigger) {
      return;
    }
    try {
      let trigger: any;
      if (eventTrigger) {
        trigger = {
          name: eventTrigger?.name,
          //TODO: Should handle case when no processID (new Process)
          processId: entityData?.processId,
          processName: entityData?.name,
          triggerType: TriggerType.EVENT,
          triggerData: {
            description: eventTrigger?.description,
          },
        };
      } else if (calendarTrigger) {
        // Ensure timezone is set - use browser's timezone as fallback if not provided
        const timezone = calendarTrigger?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

        trigger = {
          name: calendarTrigger?.name,
          //TODO: Should handle case when no processID (new Process)
          processId: entityData?.processId,
          processName: entityData?.name,
          triggerType: TriggerType.CALENDAR,
          triggerData: {
            description: calendarTrigger?.description,
            startTime:
              DateTime.fromFormat(calendarTrigger?.startTime || '', "yyyy-MM-dd'T'HH:mm", { zone: timezone }).toISO() ||
              DateTime.now().setZone(timezone).toISO(),
            endTime:
              DateTime.fromFormat(calendarTrigger?.endTime || '', "yyyy-MM-dd'T'HH:mm", { zone: timezone }).toISO() ||
              DateTime.now().setZone(timezone).plus({ minutes: 30 }).toISO(),
            timeZone: timezone, // Use timeZone (capital Z) as the primary field
            recurrence: calendarTrigger.rrule,
            attendees: calendarTrigger?.guests
              ? calendarTrigger?.guests.split(',').map((guest) => ({
                  email: guest.trim(),
                }))
              : [],
            calendarId: initialData?.service?.calendar?.id || '',
            reminder: calendarTrigger?.reminder
              ? {
                  enabled: calendarTrigger.reminder.enabled,
                  intervalValue: calendarTrigger.reminder.intervalValue,
                  intervalUnit: calendarTrigger.reminder.intervalUnit,
                }
              : undefined,
          },
        };
      }
      const query = isEditing ? TriggersGraphQL.mutations.updateTrigger : TriggersGraphQL.mutations.createTrigger;
      if (isEditing && trigger) {
        trigger.triggerId = isEditing;
      }
      const data: createOrUpdateTriggerRequest = await graphQlClient.request(query, {
        trigger,
      });
      if (data?.createTrigger?.triggerId) {
        if (!onboardingDetails?.triggerAdded) {
          updateOrganizationTriggerAdded();
        }
        setTriggers([...triggers, data.createTrigger]);
        dispatch(
          appendActionMessage({
            message: localeActionMessages['triggerCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
      if (data?.updateTrigger?.triggerId) {
        setTriggers([
          ...triggers.filter((item) => item.triggerId !== data?.updateTrigger?.triggerId),
          data.updateTrigger,
        ]);
        dispatch(
          appendActionMessage({
            message: localeActionMessages['triggerUpdatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      console.log('error', e);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setIsEditing(null);
      setTriggerSaving(false);
    }
  };

  const handleRemoveTrigger = async (id: string) => {
    setTriggerSaving(true);
    try {
      const deleteResult: deleteTriggerRequest = await graphQlClient.request(TriggersGraphQL.mutations.deleteTrigger, {
        id,
      });
      if (deleteResult?.deleteTrigger?.affected > 0) {
        setTriggers(triggers.filter((trigger: Trigger) => trigger.triggerId !== id));
      }
    } catch (e: any) {
      console.log('error', e?.response?.errors[0]?.message);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setTriggerSaving(false);
    }
  };

  const subDrawerClose = () => {
    setAddTrigger(null);
    setSelectedEventTrigger(undefined);
    setSelectedCalendarTrigger(undefined);
    setIsEditing(null);  // Reset editing state to prevent new triggers overwriting old ones
  };

  const addNewTrigger = (type: TriggerType) => {
    if (!initialData?.processId) {
      dispatch(
        appendActionMessage({
          message: localeProcessForm['saveBeforeTriggers'],
          type: SnackType.WARNING,
        }),
      );
      return;
    }
    setAddTrigger(type);
  };

  const calendarTriggerList = triggers
    ?.filter((item) => item.triggerType === TriggerType.CALENDAR)
    .map((item) => (
      <WolfTriggerBox
        key={item.triggerId}
        title={item.name}
        dateFrom={
          new Date(item.triggerData?.startTime).toLocaleDateString() +
          ' ' +
          new Date(item.triggerData?.startTime).toLocaleTimeString()
        }
        dateTo={
          new Date(item.triggerData?.endTime).toLocaleDateString() +
          ' ' +
          new Date(item.triggerData?.endTime).toLocaleTimeString()
        }
        description={item.triggerData?.description}
        recurrence={item.triggerData?.recurrence}
        onEdit={() => {
          setIsEditing(item.triggerId);
          setSelectedCalendarTrigger(item);
          setAddTrigger(TriggerType.CALENDAR);
        }}
        onDelete={() => handleRemoveTrigger(item.triggerId)}
      />
    ));

  const eventTriggerList = triggers
    ?.filter((item) => item.triggerType === TriggerType.EVENT)
    .map((item) => (
      <WolfTriggerBox
        key={item.triggerId}
        title={item.name}
        description={item.triggerData?.description}
        onEdit={() => {
          setIsEditing(item.triggerId);
          setSelectedEventTrigger({
            name: item.name,
            description: item.triggerData?.description,
          });
          setAddTrigger(TriggerType.EVENT);
        }}
        onDelete={() => handleRemoveTrigger(item.triggerId)}
      />
    ));

  if (triggerSaving) {
    return (
      <Box className="flex justify-center items-center h-full">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="w-full h-full">
      <Prompt when={hasUnsavedChanges} message={localeCommon['changesNotSaved']} />
      <List>
        {initialData?.processId && (
          <Box className="flex justify-between gap-8">
            <Box className="w-full">
              <Box className="flex items-center border-solid border-l-0 border-r-0 border-t-0 border-primaryFrozen justify-between pb-4 w-full">
                <Box className="flex items-center">
                  <Icon color="primary">
                    <CalendarTriggerIcon20 />
                  </Icon>
                  <Typography variant="body14semibold">{localeProcessForm['calTrig']}</Typography>
                </Box>
                <WolfButton
                  onClick={() => addNewTrigger(TriggerType.CALENDAR)}
                  endIcon={<AddOutlined />}
                  variant="outlined"
                  color="tertiary"
                  className="!border-frozenGrayShades2 border-2">
                  {localeCommon['add']}
                </WolfButton>
              </Box>
              {calendarTriggerList}
            </Box>
            <Box className="w-full">
              <Box className="flex items-center border-solid border-l-0 border-r-0 border-t-0 border-primaryFrozen justify-between pb-4 w-full">
                <Box className="flex items-center">
                  <Icon color="primary">
                    <ElectricBoltOutlined />
                  </Icon>
                  <Typography variant="body14semibold">{localeProcessForm['evTrig']}</Typography>
                </Box>
                <WolfButton
                  onClick={() => addNewTrigger(TriggerType.EVENT)}
                  endIcon={<AddOutlined />}
                  variant="outlined"
                  color="tertiary"
                  className="!border-frozenGrayShades2 border-2">
                  {localeCommon['add']}
                </WolfButton>
              </Box>
              {eventTriggerList}
            </Box>
          </Box>
        )}
      </List>
      <Box className="absolute border-solid border-l-0 border-r-0 border-b-0 border-primaryFrozen !h-16 bottom-0 left-0 w-full">
        {/* NOTE: border-l-0 border-r-0 border-b-0 is a workaround to avoid a conflict between mui material and tailwind */}
        <Box
          className="mt-auto bg-white flex justify-between items-center"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={3}
          py={2}>
          <WolfButton variant="outlined" color="secondary" onClick={_onClose}>
            {localeCommon['close']}
          </WolfButton>
        </Box>
      </Box>

      <CustomDrawer
        showBack={true}
        showClose={false}
        title={localeProcessForm['evTrig']}
        isOpen={addTrigger === TriggerType.EVENT}
        onClose={subDrawerClose}
        size="normal">
        <AddEventTriggerForm
          processName={entityData?.name || ''}
          onClose={subDrawerClose}
          onSave={handleAddTrigger}
          initialData={selectedEventTrigger}
          saving={triggerSaving}
        />
      </CustomDrawer>

      <CustomDrawer
        showBack={true}
        showClose={false}
        title={localeProcessForm['calTrig']}
        isOpen={addTrigger === TriggerType.CALENDAR}
        onClose={subDrawerClose}
        size="normal">
        <AddCalendarTriggerForm
          processName={entityData?.name || ''}
          onClose={subDrawerClose}
          onSave={handleAddTrigger}
          initialData={selectedCalendarTrigger}
          existingTriggers={triggers}
          saving={triggerSaving}
        />
      </CustomDrawer>

      {showConfirmLeaveModal && (
        <CustomModal
          isOpen={!!showConfirmLeaveModal}
          title={localeCommon['confirm']}
          customConfirmColor="error"
          customCancelText={localeCommon['backToEditing']}
          customConfirmText={localeCommon['discardChanges']}
          maxWidth="600px"
          showButtons={true}
          onClose={closeConfirmLeaveModal}
          onConfirm={closeAndConfirmLeaveModal}>
          <ConfirmLeaveModal />
        </CustomModal>
      )}
    </Box>
  );
};

export default AddTriggerForm;
